import { type DeepPartial, invariant, merge, uuid } from "@banjoanton/utils";
import z from "zod/v4";
import { ChessColorsSchema } from "./base-model";
import { EngineModel } from "./engine-model";
import { CaptureSummarySchema, Move, MoveScoreSchema } from "./move-model";
import { EvaluationDisplaySchema } from "./evaluation-display";
import { MultiPvLineSummarySchema } from "./multi-pv-model";

export const PositionAnalysisSchema = z.object({
    depth: z.number(),
    bestMove: z.string(),
    ponder: z.string().optional(),
    scoreBefore: MoveScoreSchema,
    scoreAfter: MoveScoreSchema,
    scoreAfterBestReply: MoveScoreSchema,
    version: z.number(),
    engineModel: z.enum(EngineModel),
    evaluationDisplay: EvaluationDisplaySchema,
    bestLines: MultiPvLineSummarySchema.optional(),
});

export type ChessPositionAnalysis = z.infer<typeof PositionAnalysisSchema>;

export const PositionSchema = z.object({
    id: z.string().default(() => uuid()),
    fen: z.string(),
    previousFen: z.string().optional(),
    nextFen: z.string().optional(),
    nextMoveColor: ChessColorsSchema,
    analysis: PositionAnalysisSchema.optional(),
    previousMove: z.string().optional(),
    nextMove: z.string().optional(),
    index: z.number(),
    captureSummary: CaptureSummarySchema,
});

export type ChessPosition = z.infer<typeof PositionSchema>;

const from = (position: ChessPosition) => position;

const fromMoves = (moves: Move[]): ChessPosition[] => {
    const positions: ChessPosition[] = [];

    if (moves.length === 0) {
        return positions;
    }

    positions.push(
        from({
            id: uuid(),
            fen: moves[0]!.fenBefore,
            index: 0,
            nextMoveColor: moves[0]!.color,
            nextFen: moves[0]!.fenAfter,
            nextMove: moves[0]!.id,
            previousFen: undefined,
            previousMove: undefined,
            captureSummary: structuredClone(moves[0]!.captureSummary),
        })
    );

    let previousColor = moves[0]!.color;

    const flipColor = () => {
        previousColor = previousColor === "w" ? "b" : "w";
    };

    for (let i = 0; i < moves.length; i++) {
        const move = moves[i];
        invariant(move, "Move should be defined here");
        const nextMove = moves[i + 1];

        flipColor();

        positions.push(
            from({
                id: uuid(),
                fen: move.fenAfter,
                index: i + 1,
                nextMoveColor: previousColor,
                nextFen: nextMove?.fenAfter,
                nextMove: nextMove?.id,
                previousFen: move.fenBefore,
                previousMove: move.id,
                captureSummary: structuredClone(move.captureSummary),
            })
        );
    }

    return positions;
};

const update = (current: ChessPosition, updated: DeepPartial<ChessPosition>) =>
    merge(current, updated);

export const Position = { from, fromMoves, update };
